import React, { useState } from "react";
import { Badge } from "../UI/Badge";
import { MAIN } from "../../constants/styles";
import { useGetOrderDetails } from "../../hooks/useProducts";
import { Link } from "react-router-dom";
import {
  dateFormatter,
  numberFormatter,
  paymentMethodEnum,
} from "../../helper/helpers";
import { useMutation } from "@tanstack/react-query";
import { initiatePaymentApi } from "../../services/products";
import { toast } from "sonner";
import {
  AlertComponent,
  AlertErrorComponent,
  AlertSuccessComponent,
} from "../UI/AlertComponent";
import { statusText } from "../UI/StatusText";
import { slugify } from "../../utils/slugify";

// Props: order (object), onBack (function), isLoading (boolean), isError (boolean)
const SectionOrderDetails = ({ onBack, isLoading, isError, orderId }) => {
  const {
    data = {},
    isPending,
    isError: ordersIsError,
    error: ordersError,
  } = useGetOrderDetails(orderId);

  const [errorArray, setErrorArray] = useState([]);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  // if (!order) return null;

  let { order = {} } = data;
  let { items = [] } = order;
  let { shippingAddress = {} } = order;
  let { payment = {} } = order;

  const statusBadge = (status) => {
    switch (status) {
      case "Delivered":
        return Badge.green(statusText(status));
      case "Pending":
        return Badge.main(statusText(status));
      case "Cancelled":
        return Badge.red(statusText(status));
      default:
        return Badge.blue(statusText(status));
    }
  };

  const { mutate: CheckoutBasketPayment, isPending: isLoadingPayment } =
    useMutation({
      mutationFn: () => initiatePaymentApi(order?.id),
      onSuccess: (data) => {
        setSuccessMsg(data?.message);
        setErrorMsg("");
        toast.success("Payment initiated succesfully");
      },
      onError: (err) => {
        setErrorMsg(err);
        toast.error(err || "Unable to initiate payment!");
      },
      onSettled: () => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth", // for smooth scrolling
        });
      },
    });

  const handlePayment = () => {
    setSuccessMsg("");
    CheckoutBasketPayment();
  };

  React.useEffect(() => {
    const errs = [];

    if (ordersIsError) errs.push(`orders: ${ordersError}`);

    setErrorArray(errs);
  }, [ordersIsError, ordersError]);

  if (isPending) return <p>Loading order details...</p>;
  if (ordersIsError) return <p>Failed to load order details {ordersError}</p>;

  return (
    <div className="container py-2">
      {errorMsg && <AlertErrorComponent errorMsg={errorMsg} />}
      {successMsg && <AlertSuccessComponent message={successMsg} />}
      {ordersIsError && <AlertComponent errorArray={errorArray} />}
      <button
        onClick={onBack}
        style={{
          marginBottom: 10,
          background: "none",
          border: "none",
          color: MAIN,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        ← Back to Orders
      </button>

      {/* HEADER */}
      <h6 className="mb-2 fw-bold">Order History</h6>
      <div className="d-flex justify-content-betwwen">
        <div>
          {/* STATUS */}
          <div className="my-5">{statusBadge(order.status)}</div>
          <p className="fw-bold">Order nº {order?.orderNumber}</p>
          <p className="text-sm">{order?.items?.length} Items Orders</p>
          <p className="text-sm mt-5 mb-0 fw-semibold">
            Placed On{" "}
            {order?.createdAt &&
              dateFormatter.format(new Date(order?.createdAt))}
          </p>
        </div>

        {order?.status === 3 && payment?.method === 1 && (
          <div className="ms-auto">
            <button
              type="button"
              className="btn btn-sm btn-main mt-40 rounded-8 me-md-2"
              disabled={isPending || isLoadingPayment}
              onClick={handlePayment}
            >
              {isPending && (
                <div
                  className="spinner-border spinner-border-sm me-5"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
              <img
                src="/assets/images/icon/mpesa-white.png"
                alt="mpesa"
                width={50}
              />
            </button>
          </div>
        )}
      </div>
      <hr />
      <p className="text-md fw-medium">Items in your order</p>
      <hr />
      <ul className="list-group list-group-flush mb-10">
        {items.map((product) => (
          <li className="list-group-item" key={product.orderId}>
            <div className="d-flex justify-content-between">
              <div className="hstack gap-20 flex-fill">
                <span className="table-product__thumb border border-gray-100 rounded-8 flex-center ">
                  <img
                    src="/assets/images/thumbs/product-two-img1.png"
                    // src={item.imageUrl}
                    alt=""
                    className="img-fluid"
                  />
                </span>
                <span className="table-product__content text-start">
                  <h6 className="title text-lg fw-semibold mb-8">
                    <Link
                      to={`/products/product-details/${product?.productId}?name=${slugify(product?.productName)}`}
                      // to={`/products/product-details/${slugify(product?.productName)}`}
                      state={product}
                      className="link text-line-2"
                      tabIndex={0}
                    >
                      {product?.productName}
                    </Link>
                  </h6>
                  <div className="vstack my-5 gap-6">
                    <span className="py-2 text-sm fw-semibold">
                      QTY: {"  "}
                      {product?.quantity}
                    </span>
                    <span className="text-sm mt-5 mb-0 fw-semibold">
                      {numberFormatter.format(Number(product?.price))}
                    </span>
                  </div>
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="row">
        <div className="col-md">
          <div className="card h-100">
            <div className="card-header">
              <p className="m-0">Payment Information</p>
            </div>
            <div className="card-body">
              <div className="my-20">
                <p className="fw-bold">Payment Method</p>
                <p>Pay Now With {paymentMethodEnum[payment?.method]}</p>
              </div>
              <div className="my-20">
                <p className="fw-bold">Payment Details</p>
                <p>Phone No: {payment?.phoneNumber}</p>
                <p>Transaction Ref: {payment?.transactionReference}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md">
          <div className="card h-100">
            <div className="card-header">
              <p className="m-0">Delivery Method</p>
            </div>
            <div className="card-body">
              <div className="my-20">
                <p className="fw-bold">Shipping Address</p>
                <p>Door Delivery</p>
              </div>
              <div className="my-20">
                <p className="fw-bold">Delivery Information</p>
                <p className="m-0">
                  {shippingAddress?.firstName} {shippingAddress?.lastName}
                </p>
                <p className="m-0">
                  {shippingAddress?.addressLine}, {shippingAddress?.postalCode}
                </p>
                <p className="m-0">{shippingAddress?.landmark}</p>
                <p className="m-0">
                  {shippingAddress?.city}, {shippingAddress?.country}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionOrderDetails;
